import Vuex from 'vuex'
import VueI18n from 'vue-i18n'
import fscreen from 'fscreen'
import camelcaseKeys from 'camelcase-keys'

import User from '../data/user'
import ElementUIWrapper from '../lib/element_wrapper'
import { ExerciseResponse } from '@src/data/exercise-response'
import Station from '@src/data/station'
import WorkoutData from '@src/data/workout-data'
import { WorkoutExercise, WorkoutExerciseConfig } from '@src/data/workout-exercise'
import TrainingPlan, { TrainingDay } from '@src/data/training-plan'
import Note from '@src/data/note'
import Club from '@src/data/club'
import Activity from '@src/data/activity'
import { HealthGoal } from '@src/data/goal'
import moment from 'moment'
import '@src/util/moment-locales'
import Checkup from '@src/data/checkup'
import Task from '@src/data/task'
import { NotificationMessage } from '@src/data/notification'
import { MemberProfile } from '@src/data/member-profile'
import { localeSpecifications, SCORE_TYPES } from '@src/util/constants'
import {
  defaultExerciseFilters,
  defaultInfiniteScrollState,
  defaultMemberFilters,
  defaultMemberNoteFilters,
  defaultTrainingPlanFilters,
  defaultWorkoutFilters,
  defaultWorkoutSelectorFilters
} from '@src/state/dafaults'
import { formatTPDays } from '@src/util/functions'
import { MemberActivityType } from '@src/routes/members/member-activities'
import UserClubSummary from '@src/data/user-club-summary'
import LeaderBoard from '@src/data/leader-board'
import MessagesProvider from '@src/providers/messages'
import { EmojiType } from '@src/routes/club/emoji/types/EmojiType'
import Emoji from '@src/routes/club/emoji/types/Emoji'
import EmojiData from '@src/routes/club/emoji/types/EmojiData'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'
import { DEFAULT_WORKOUT_DATA } from '@src/routes/content/live-workout-data-retriever'
import { safeParseJsonAndClean } from '@src/util/safeJson'
import { FavoriteExerciseService } from '@src/service/favorite-exercise'

export type MemberFilters = {
  name?: string
  state?: 'published' | 'deactivated' | 'intern'
  toBeDeactivated?: boolean
  flags: Set<string>
  page: number
}

export type NoteFilters = {
  startDate?: string
  endDate?: string
  word?: string
}

export type WorkoutFilters = {
  name?: string
  type?: 'home' | 'station' | 'certified' | 'non-certified' | 'member-trainer' | 'hidden' | 'show'
  from?: string,
  to?: string,
  categories?: number[]
  page: number
}

export type WorkoutsSelectorFilters = {
  name?: string
  type?: 'home' | 'station' | 'certified' | 'non-certified' | 'member-trainer'
  tags?: Set<string>
}

export type ExerciseFilters = {
  tags?: ExerciseTagFilter,
  name?: string,
  page: number,
  favoritesOnly?: boolean
}

export type ExerciseTagFilter = {
  movement?: Set<string>
  focus?: Set<string>
  equipment?: Set<string>
  muscle?: Set<string>
  body?: Set<string>
  phase?: Set<string>
  health?: Set<string>
  category?: Set<string>
  clinical?: Set<string>
  execution?: Set<string>
  exercise?: Set<string>
  joints?: Set<string>
  range?: Set<string>
}

export type TaskFilters = {
  name?: string
  dueDate?: string
  status?: string
  days?: number
}

export type MemberActivityFilters = {
  type: MemberActivityType | ''
  status: Set<string>
}

export class TrainingPlanBuilder {
  type: string
  trainingDays: TrainingDay[] = []
  availableWorkouts: WorkoutData[] = []
  selectedTrainingPlan: TrainingPlan = new TrainingPlan()
  updated: boolean = false
}

export type TrainingPlanFilters = {
  name?: string
  type: 'home' | 'tracked' | 'club' | 'member' | 'all'
  page: number
}

export type BlogPostFilters = {
  text?: string
  from?: Date
  to?: Date
}

export type Toast = {
  show: boolean
  message: string
  fadeOut: boolean
  actionText?: string
  actionCallback?: (...params) => void
}

export type InfiniteScrollState = {
  loading: boolean
  displayErrorMessage: boolean
  endOfPageReached: boolean
}

export type WorkoutLiveData = {
  elapsedTime: number,
  state: string,
  repetitions: number,
  previewTime?: number,
  currentExerciseIndex: number
  isWorkoutPaused: boolean,
  totalElapsedTime: number,
  exerciseDone: boolean,
  workoutDone: boolean
}

export interface DispatchableActions {
  // root module
  (type: 'switchFullscreen', fullscreen: boolean)

  (type: 'login', user: User)

  (type: 'logout')

  (type: 'switchLanguage', language: string)

  (type: 'startWorkout')

  (type: 'finishWorkout')

  (type: 'advanceWorkout', previousPage: string)

  (type: 'setOverviewMessage', overviewMessage: string)

  (type: 'filterMembers', by: MemberFilters)

  (type: 'filterWorkouts', by: WorkoutFilters)

  (type: 'filterExercises', by: ExerciseFilters)

  (type: 'filterTrainingPlans', by: TrainingPlanFilters)

  (type: 'filterWorkoutSelector', by: WorkoutsSelectorFilters)

  (type: 'filterTasks', by: TaskFilters)

  (type: 'filterMemberActivities', by: MemberActivityFilters)

  (type: 'filterBlogPosts', by: BlogPostFilters)

  (type: 'showToast', { message, fadeOut, actionText, actionCallback })

  (type: 'closeToast')

  (type: 'changeStep', page: number)
}

export type Member = {
  code: string,
  first_name: string,
  last_name: string,
  gender: string,
  email: string,
  birthdate: string
}

// needed when changing the active workouts/training plan for a member
export type NewActiveTrainingWorkouts = {
  newCustomWorkoutId: number
  newHomeWorkoutId: number
  newTrainingPlanId: number
  newTrainingPlanName: string
  newTrainingPlanTotalDays: number
}

export interface RootState {
  fullscreen: boolean
  toast: Toast
  toastTimeoutEvent: any
  user: User | null
  i18n: VueI18n
  new_workout: WorkoutData
  stations: Station[]
  new_training_plan: TrainingPlanBuilder | null
  exerciseTags: any | null,
  completeExerciseTags: any | null,
  note: Note | null,
  trainingMember: Member | null
  workoutMember: Member | null
  club: Club | null,
  activities: Activity[],
  goal: HealthGoal | null,
  availableExercises: ExerciseResponse[]
  loadAvailableExercisesComplete: boolean,
  favoriteExerciseIds: Set<number>,
  checkup: Checkup | null,
  predefinedHealthGoals: HealthGoal[],
  memberWeight: number | null,
  memberHeight: number | null,
  task: Task | null,
  tasks: Task[],
  selectedMember: Member | null,
  filteredNotes: Note[],
  notifications: NotificationMessage[],
  clubStations: Station[]
  newActiveTrainingWorkouts: NewActiveTrainingWorkouts,
  memberProfile: MemberProfile | null,
  activePlanStartDate: string,
  step: number | null,
  overviewMessage: string
  memberFilters: MemberFilters
  memberNoteFilters: NoteFilters
  workoutFilters: WorkoutFilters
  exerciseFilters: ExerciseFilters
  trainingPlansFilters: TrainingPlanFilters
  workoutSelectorFilters: WorkoutsSelectorFilters,
  infiniteScrollState: InfiniteScrollState,
  positionToFocusBuilderOn: number | null
  isSidebarVisible: boolean,
  statusScoreChartSelectedFilters: string[],
  userClubSummary: UserClubSummary,
  leaderBoard: LeaderBoard,
  emojiData: EmojiData,
  emojiActiveType: EmojiType,
  expandedMemberIndex: number,
  datePickerActiveTypeFilter: DateFilterType,
  workoutLiveData: WorkoutLiveData,
  workouts: WorkoutData[]
}

function createWorkoutExerciseFromExercise (exerciseData: ExerciseResponse, club: Club | null, circleWorkout: boolean): WorkoutExercise {
  const exercise = new ExerciseResponse(exerciseData)
  const workoutExercise = new WorkoutExercise()
  workoutExercise.setExercise(exercise)

  const initialExercise = new WorkoutExerciseConfig()
  initialExercise.setDefaultValues(exercise)

  if (!circleWorkout && club) {
    const repetitions = workoutExercise.getDefaultRepetitionsCount(club.screenExerciseLen)
    workoutExercise.repetitions = repetitions
    initialExercise.repetitions = repetitions
  }

  workoutExercise.initialExerciseConfig = initialExercise
  return workoutExercise
}

class Store extends Vuex.Store<RootState> {

  dispatch: DispatchableActions

  constructor (user: User | null, i18n) {
    super({
      state: {
        fullscreen: false,
        toast: { show: false, message: '', fadeOut: false },
        toastTimeoutEvent: null,
        user: user,
        i18n: i18n,
        new_workout: new WorkoutData({}),
        stations: [],
        new_training_plan: new TrainingPlanBuilder(),
        exerciseTags: [],
        completeExerciseTags: null,
        note: null,
        trainingMember: null,
        workoutMember: null,
        club: user ? new Club(camelcaseKeys(user.club, { deep: true })) : null,
        activities: [],
        goal: null,
        availableExercises: safeParseJsonAndClean<any[]>(sessionStorage, 'exercises', [])
          .map(exercise => new ExerciseResponse(exercise)),
        loadAvailableExercisesComplete: sessionStorage.getItem('exercises') !== null,
        favoriteExerciseIds: new Set<number>(),
        checkup: null,
        predefinedHealthGoals: [],
        memberWeight: null,
        memberHeight: null,
        task: null,
        tasks: [],
        selectedMember: null,
        filteredNotes: [],
        notifications: [],
        clubStations: [],
        newActiveTrainingWorkouts: {
          newCustomWorkoutId: 0,
          newHomeWorkoutId: 0,
          newTrainingPlanId: 0,
          newTrainingPlanName: '',
          newTrainingPlanTotalDays: 0
        },
        memberProfile: null,
        activePlanStartDate: '',
        step: null,
        overviewMessage: '',
        memberFilters: defaultMemberFilters(),
        memberNoteFilters: defaultMemberNoteFilters(),
        workoutFilters: defaultWorkoutFilters(),
        exerciseFilters: defaultExerciseFilters(),
        trainingPlansFilters: defaultTrainingPlanFilters(),
        workoutSelectorFilters: defaultWorkoutSelectorFilters(),
        infiniteScrollState: defaultInfiniteScrollState(),
        positionToFocusBuilderOn: null,
        isSidebarVisible: false,
        statusScoreChartSelectedFilters: Object.assign([], SCORE_TYPES) || /* istanbul ignore next: Object.assign always returns its target */ [],
        userClubSummary: new UserClubSummary(),
        leaderBoard: new LeaderBoard(),
        emojiData: new EmojiData(),
        emojiActiveType: EmojiType.DEFAULT,
        expandedMemberIndex: -1,
        datePickerActiveTypeFilter: DateFilterType.WEEK,
        workoutLiveData: DEFAULT_WORKOUT_DATA,
        workouts: []
      },

      getters: {
        fullscreen: (state) => {
          return state.fullscreen
        },
        dispatch: () => {
          return this.dispatch
        },
        exerciseTags: (state) => {
          return state.exerciseTags
        }
      },

      mutations: {
        fullscreen: (state, fullscreen: boolean) => {
          state.fullscreen = fullscreen
        },
        toast: (state, toast: Toast) => {
          if (state.toast.show === true) {
            clearTimeout(state.toastTimeoutEvent)
          }
          state.toast = toast
        },
        toastTimeoutEvent: (state, event: any) => {
          state.toastTimeoutEvent = event
        },
        setUser: (state, user) => {
          state.user = user
        },
        language: (state, locale) => {
          const projects = ['app', 'exercises', 'invariants']
          let messagesProvider = new MessagesProvider([locale], projects)
          // fetch new language requirements
          messagesProvider.fetchMessages()
            .then(async (messages) => {
              Object.entries(messages).forEach(([key, value]) => {
                state.i18n.setLocaleMessage(key, value)
              })
              Store.configMoment(locale)
              state.i18n.locale = locale
              await ElementUIWrapper.init([locale])
              ElementUIWrapper.locale = locale

            })
            .catch(() => {
              console.error('Failed to load new language')
            })
        },
        addSingleExerciseToWorkout: (state, data) => {
          state.new_workout.workoutExercises = []
          if (data.exercise) {
            let workoutExercise = new WorkoutExercise()
            workoutExercise.setExercise(new ExerciseResponse(data.exercise))

            let initialExercise = new WorkoutExerciseConfig()
            initialExercise.setDefaultValues(data.exercise)
            workoutExercise.initialExerciseConfig = initialExercise

            state.new_workout.workoutExercises.push(workoutExercise)
          }
        },
        // Workout builder
        addExerciseToWorkout: (state, data) => {
          const workoutExercise = createWorkoutExerciseFromExercise(data.exercise, state.club, state.new_workout.circleWorkout)

          if (data.index) {
            state.new_workout.workoutExercises.splice(data.index, 0, workoutExercise)
            state.positionToFocusBuilderOn = data.index
          } else {
            state.new_workout.workoutExercises.push(workoutExercise)
            state.positionToFocusBuilderOn = state.new_workout.workoutExercises.length - 1
          }
        },
        removeExercisesFromWorkout: (state) => {
          state.new_workout.workoutExercises = []
        },

        setGeneratedWorkoutFromExercises: (state, data: {
          exercises: ExerciseResponse[],
          mode?: 'replace' | 'append',
          workoutName?: string,
          continuousWorkout?: boolean,
          circleWorkout?: boolean,
          stationLocked?: boolean
        }) => {
          if (typeof data.continuousWorkout === 'boolean') {
            state.new_workout.continuousWorkout = data.continuousWorkout
          }
          if (typeof data.circleWorkout === 'boolean') {
            state.new_workout.circleWorkout = data.circleWorkout
          }
          if (typeof data.stationLocked === 'boolean') {
            state.new_workout.stationLocked = data.stationLocked
          }
          if (typeof data.workoutName === 'string') {
            state.new_workout.name = data.workoutName
          }

          const existingExercises = data.mode === 'append'
            ? state.new_workout.workoutExercises.slice()
            : []
          const generatedExercises = (data.exercises || []).map(exercise =>
            createWorkoutExerciseFromExercise(exercise, state.club, state.new_workout.circleWorkout)
          )
          state.new_workout.workoutExercises = existingExercises.concat(generatedExercises)
          state.positionToFocusBuilderOn = state.new_workout.workoutExercises.length
            ? state.new_workout.workoutExercises.length - 1
            : null
        },

        removeExercisesByIdFromWorkout: (state, ids: any[]) => {
          ids.forEach(id => {
            const index = state.new_workout.workoutExercises.findIndex(exercise => exercise.id === id)
            if (index !== -1) {
              const workout = state.new_workout.workoutExercises.splice(index, 1)
              console.log('removed successfully', workout)
              return
            }
            console.log('didnt find the index')
          })
        },
        setAvailableExercises: (state, exercises) => {
          state.availableExercises = exercises
          sessionStorage.setItem('exercises', JSON.stringify(exercises))
          state.loadAvailableExercisesComplete = true

          // Extract favorite IDs from exercises (if backend provides is_favorite)
          const favoriteIds = exercises
            .filter(ex => ex.isFavorite === true)
            .map(ex => ex.id)
          if (favoriteIds.length > 0) {
            state.favoriteExerciseIds = new Set(favoriteIds)
          }
        },
        SET_FAVORITE_EXERCISES: (state, favoriteIds: number[]) => {
          state.favoriteExerciseIds = new Set(favoriteIds)
          // Also update isFavorite on each exercise object
          state.availableExercises.forEach(exercise => {
            exercise.isFavorite = favoriteIds.includes(exercise.id)
          })
          // Update sessionStorage to persist the change
          sessionStorage.setItem('exercises', JSON.stringify(state.availableExercises))
        },
        ADD_FAVORITE_EXERCISE: (state, exerciseId: number) => {
          state.favoriteExerciseIds.add(exerciseId)
          // Update exercise object directly
          const exercise = state.availableExercises.find(ex => ex.id === exerciseId)
          if (exercise) {
            exercise.isFavorite = true
          }
          sessionStorage.setItem('exercises', JSON.stringify(state.availableExercises))
        },
        REMOVE_FAVORITE_EXERCISE: (state, exerciseId: number) => {
          state.favoriteExerciseIds.delete(exerciseId)
          // Update exercise object directly
          const exercise = state.availableExercises.find(ex => ex.id === exerciseId)
          if (exercise) {
            exercise.isFavorite = false
          }
          // Update sessionStorage to persist the change
          sessionStorage.setItem('exercises', JSON.stringify(state.availableExercises))
        },

        removeExerciseFromWorkout: (state, index) => {
          if (index > -1) {
            state.new_workout.workoutExercises.splice(index, 1)
          }
        },
        changeExercisePosition: (state, { index1, index2 }) => {
          state.new_workout.workoutExercises.splice(index2, 0, state.new_workout.workoutExercises.splice(index1, 1)[0])
        },
        changeExerciseWeight: (state, { index, weight }) => {
          state.new_workout.workoutExercises[index].weight = weight
        },
        changeExerciseBreakpoint: (state, { index, value }) => {
          state.new_workout.workoutExercises[index].breakpointAfterExercise = value
        },
        changeExerciseDuration: (state, { index, repetitionDuration }) => {
          state.new_workout.workoutExercises[index].repetitionDuration = repetitionDuration
        },
        changeRepetitionsCount: (state, { index, repetitions }) => {
          state.new_workout.workoutExercises[index].repetitions = repetitions
        },
        changeSubsequentRestPeriod: (state, { index, subsequentRestPeriod }) => {
          state.new_workout.workoutExercises[index].subsequentRestPeriod = subsequentRestPeriod
        },
        changeSide: (state, { index, side }) => {
          state.new_workout.workoutExercises[index].side = side
        },
        setWorkoutType: (state, type) => {
          state.new_workout.type = type
        },
        setContinuousWorkout: (state, isContinuousWorkout) => {
          state.new_workout.continuousWorkout = isContinuousWorkout
        },
        setCircleWorkout: (state, isCircleWorkout) => {
          state.new_workout.circleWorkout = isCircleWorkout
        },
        setStationDependence: (state, stationDependence) => {
          state.new_workout.stationLocked = stationDependence
        },
        setRestartWorkout: (state, isRestartWorkout) => {
          state.new_workout.restartIfUncompleted = isRestartWorkout
        },
        setSelectedWorkout: (state, { workout, exercises }) => {
          state.new_workout = workout
          state.new_workout.workoutExercises = exercises
          exercises.forEach(exercise => {
            for (let i = 0; i < state.stations.length; i++) {
              if (state.stations[i].id === exercise.stationId) {
                state.stations[i].exercises.push(exercise)
              }
            }
          })
          state.new_workout.workoutExercises.forEach((currentExercise) => {
            let initialExercise = new WorkoutExerciseConfig(currentExercise)
            initialExercise.setDefaultValues(currentExercise.exercise)
            if (state.new_workout.circleWorkout) {
              // obs: repetitions needs to be null when workout is circle
              currentExercise.repetitions = null
            }
            currentExercise.initialExerciseConfig = initialExercise
          })
        },
        setSelectedTrainingPlan: async (state, { trainingPlan }) => {
          state.new_training_plan!.updated = false
          state.new_training_plan!.selectedTrainingPlan = trainingPlan
          state.new_training_plan!.type = trainingPlan.trainingPlanType

          state.new_training_plan!.trainingDays = await formatTPDays(trainingPlan.startDate, trainingPlan.trainingDays)
        },
        setAvailableWorkouts: (state, workouts) => {
          state.new_training_plan!.availableWorkouts = workouts
        },
        setTrainingPlanDays: (state, days) => {
          state.new_training_plan!.trainingDays = days
        },
        addWorkoutToTrainingDay: (state, workout) => {
          let trainingDays = state.new_training_plan!.trainingDays
          let emptyDays = trainingDays.filter(function (day) {
            // search for either an empty day, or the first not immutable day
            return day.workoutAsArray.length === 0 && !day.immutable
          })
          // add selected workout to the first empty day, or the first immutable day
          if (emptyDays.length > 0) {
            state.new_training_plan!.trainingDays[emptyDays[0].position - 1].workoutAsArray.push(workout)
            state.positionToFocusBuilderOn = emptyDays[0].position - 1
          } else {
            // if no empty day, create a new training day
            let emptyDay = new TrainingDay({
              position: trainingDays.length + 1,
              restDays: 0,
              workout: null,
              workoutAsArray: [workout]
            })
            state.new_training_plan!.trainingDays.push(emptyDay)
            state.positionToFocusBuilderOn = this.state.new_training_plan!.trainingDays.length - 1
          }
          state.new_training_plan!.updated = true
        },
        removeNewWorkout: (state) => {
          state.new_workout = new WorkoutData({})
          state.stations = []
        },

        // Club stations
        setStations: (state, stations) => {
          // if (stations.length === 0) {
            // in case there is are no stations, the workout can only be 'home' workout
          //  state.new_workout.type = WorkoutType.HOME
          // }
          state.stations = stations.sort((s1,s2) => (s1.position > s2.position) ? 1 : (s1.position < s2.position) ? -1 : 0) || []

          state.new_workout.workoutExercises.forEach(exercise => {
            for (let i = 0; i < state.stations.length; i++) {
              if (state.stations[i].id === exercise.stationId) {
                state.stations[i].exercises.push(exercise)
              }
            }
          })
        },

        removeNewTrainingPlan: (state) => {
          state.new_training_plan = new TrainingPlanBuilder()
        },

        setExerciseTags: (state, exerciseTags) => {
          state.exerciseTags = exerciseTags
        },

        setCompleteExerciseTags: (state, exerciseTags) => {
          state.completeExerciseTags = exerciseTags
        },

        setNote: (state, note: Note | null) => {
          state.note = note
        },

        setMemberForActiveTraining: (state, member: Member | null) => {
          state.trainingMember = member
        },

        setMemberForActiveWorkout: (state, member: Member | null) => {
          state.workoutMember = member
        },

        setClub: (state, club) => {
          state.club = club
        },

        setActivities: (state, activities: Activity[]) => {
          state.activities = activities
        },

        setGoal: (state, goal: HealthGoal | null) => {
          state.goal = goal
        },

        setCheckup: (state, checkup) => {
          state.checkup = checkup
        },

        addNoteIdToCheckup: (state, noteId: number) => {
          state.checkup!.noteIds.push(noteId)
        },

        setPredefinedHealthGoals: (state, healthGoals: HealthGoal[]) => {
          state.predefinedHealthGoals = healthGoals
        },

        setMemberWeightHeight: (state, { weight, height }) => {
          state.memberWeight = weight
          state.memberHeight = height
        },

        setSelectedMember: (state, member: Member | null) => {
          state.selectedMember = member
        },

        updateActiveTrainingWorkouts: (state, newAttributes: Object) => {
          state.newActiveTrainingWorkouts = Object.assign(state.newActiveTrainingWorkouts, newAttributes)
        },

        setFilteredNotes: (state, notes) => {
          state.filteredNotes = notes
        },

        addNotification: (state, notification: NotificationMessage) => {
          state.notifications = [notification, ...state.notifications]
        },

        removeNotifications: (state) => {
          state.notifications = []
        },

        setTask: (state, task: Task | null) => {
          state.task = task
        },

        setClubStations: (state, stations: Station[]) => {
          state.clubStations = stations
        },

        setCheckupWorkout: (state, workoutId: number) => {
          if (state.checkup) {
            state.checkup = new Checkup({ ...state.checkup, workoutAdded: true, workoutId: workoutId })
          }
        },

        setCheckupTrainingPlan: (state, trainingPlanId: number) => {
          if (state.checkup) {
            state.checkup = new Checkup({ ...state.checkup, trainingPlanAdded: true, trainingPlanId: trainingPlanId })
          }
        },

        setProfile: (state, memberProfile: MemberProfile) => {
          state.memberProfile = memberProfile
        },

        setActivePlanStartDate: (state, startDate: string) => {
          state.activePlanStartDate = startDate
        },

        markTrainingPlanAsUpdated: (state) => {
          state.new_training_plan!.updated = true
        },

        changeStep: (state, step: number) => {
          state.step = step
        },

        setOverviewMessage: (state, overviewMessage: string) => {
          state.overviewMessage = overviewMessage
        },

        setMemberFilters: (state, filters: MemberFilters) => {
          state.memberFilters = { ...state.memberFilters, ...filters, page: filters.page ? filters.page : 1 }
        },

        setMemberNotesFilters: (state, filters: NoteFilters) => {
          state.memberNoteFilters = { ...state.memberNoteFilters, ...filters }
        },

        setWorkoutFilters: (state, filters: WorkoutFilters) => {
          state.workoutFilters = { ...state.workoutFilters, ...filters, page: filters.page ? filters.page : 1 }
        },

        setExerciseFilters: (state, filters: ExerciseFilters) => {
          state.exerciseFilters = { ...state.exerciseFilters, ...filters, page: filters.page ? filters.page : 1 }
        },

        setTrainingPlansFilters: (state, filters: TrainingPlanFilters) => {
          state.trainingPlansFilters = { ...state.trainingPlansFilters, ...filters, page: filters.page ? filters.page : 1 }
        },

        setWorkoutSelectorFilters: (state, filters: WorkoutsSelectorFilters) => {
          state.workoutSelectorFilters = { ...state.workoutSelectorFilters, ...filters }
        },

        setSidebarVisibility: (state, sidebarVisibility) => {
          state.isSidebarVisible = sidebarVisibility
        },

        setStatusScoreChartSelectedFilters: (state, statusScoreChartSelectedFilters) => {
          state.statusScoreChartSelectedFilters = statusScoreChartSelectedFilters
        },

        setUserClubSummary: (state, userClubSummary) => {
          state.userClubSummary = userClubSummary
        },

        setLeaderBoard: (state, leaderBoard) => {
          state.leaderBoard = leaderBoard
        },

        setEmojiActiveType: (state, activeType) => {
          state.emojiActiveType = activeType
          if (activeType === EmojiType.DEFAULT) {
            const defaultEmoji = state.emojiData[state.emojiActiveType].available[0]
            state!.club!.smileyId = defaultEmoji.id
            state.emojiData[state.emojiActiveType].selected = defaultEmoji
          }
        },

        setEmojiData: (state, data) => {
          state.emojiData = data
        },

        setSelectedEmoji: (state, emoji) => {
          state!.club!.smileyId = emoji.id
          state.emojiData[state.emojiActiveType].selected = emoji
        },

        setExpandedMemberIndex: (state, index) => {
          state.expandedMemberIndex = index
        },

        setDatepickerActiveTypeFilter: (state, filter) => {
          state.datePickerActiveTypeFilter = filter
        },

        setWorkoutLiveData: (state, data) => {
          state.workoutLiveData = {
            ...state.workoutLiveData,
            ...data
          }
        },

        REORDER_WORKOUTS (state, { oldIndex, newIndex }) {
          const workouts = [...state.workouts]
          const [movedWorkout] = workouts.splice(oldIndex, 1)
          workouts.splice(newIndex, 0, movedWorkout)
          state.workouts = workouts
        }
      },

      actions: {
        switchFullscreen: (context, fullscreen = true) => {
          if (fscreen.fullscreenEnabled) {
            const app = document.getElementById('app')

            fscreen.addEventListener('fullscreenchange', () => {
              context.commit('fullscreen', fscreen.fullscreenElement !== null)
            }, false)

            if (fullscreen) {
              fscreen.requestFullscreen(app)
            } else {
              fscreen.exitFullscreen(app)
            }
          }
        },

        showToast: (context, { message, fadeOut, actionText, actionCallback }: { message: string, fadeOut: boolean, actionText?: string, actionCallback?: (...params) => void }) => {
          context.commit('toast', { show: true, message, fadeOut, actionText, actionCallback })
          if (fadeOut) {
            const timeoutEvent = setTimeout(() => {
              context.commit('toast', { show: false, message, fadeOut, actionText, actionCallback })

            }, 4000)
            context.commit('toastTimeoutEvent', timeoutEvent)
          }
        },

        closeToast: (context) => {
          context.commit('toast', { show: false, message: '', fadeOut: true })
        },

        switchLanguage: (context, language) => {
          context.commit('language', language)
        },

        login: (context, user) => {
          context.commit('setUser', user)
        },

        logout: (context) => {
          context.commit('setUser', null)
          context.commit('setProfile', null)
          context.commit('setSelectedMember', null)
          context.commit('setAvailableExercises', [])
        },

        addSingleExerciseToWorkout: (context, exercise) => {
          context.commit('addSingleExerciseToWorkout', exercise)
        },

        addExerciseToWorkout: (context, exercise) => {
          context.commit('addExerciseToWorkout', exercise)
        },

        setGeneratedWorkoutFromExercises: (context, data) => {
          context.commit('setGeneratedWorkoutFromExercises', data)
        },

        removeExerciseFromWorkout: (context, index) => {
          context.commit('removeExerciseFromWorkout', index)
        },

        changeExercisePosition: (context, { index1, index2 }) => {
          context.commit('changeExercisePosition', { index1, index2 })
        },

        setAvailableExercises: (context, exercises) => {
          context.commit('setAvailableExercises', exercises)
        },

        loadFavoriteExercises: async (context) => {
          const service = new FavoriteExerciseService()
          try {
            const favoriteIds = await service.getFavoriteExercises()
            context.commit('SET_FAVORITE_EXERCISES', favoriteIds)
          } catch (error) {
            console.error('Failed to load favorite exercises', error)
          }
        },

        favouriteExercise: async (context, exercise: ExerciseResponse) => {
          console.log('🌟 STORE ACTION: favouriteExercise called for exercise ID:', exercise.id)
          const service = new FavoriteExerciseService()
          try {
            console.log('🌟 Making API call to add favorite...')
            await service.addFavorite(exercise.id)
            console.log('🌟 API call successful, updating store...')
            context.commit('ADD_FAVORITE_EXERCISE', exercise.id)
            context.dispatch('showToast', {
              message: context.state.i18n.t('PAGE.CONTENT.EXERCISES.ADDED_TO_FAVORITES'),
              fadeOut: true
            })
          } catch (error) {
            console.error('❌ Failed to favorite exercise', error)
            context.dispatch('showToast', {
              message: context.state.i18n.t('ERROR.GENERIC'),
              fadeOut: true
            })
          }
        },

        unfavouriteExercise: async (context, exercise: ExerciseResponse) => {
          console.log('⭐ STORE ACTION: unfavouriteExercise called for exercise ID:', exercise.id)
          const service = new FavoriteExerciseService()
          try {
            console.log('⭐ Making API call to remove favorite...')
            await service.removeFavorite(exercise.id)
            console.log('⭐ API call successful, updating store...')
            context.commit('REMOVE_FAVORITE_EXERCISE', exercise.id)
            context.dispatch('showToast', {
              message: context.state.i18n.t('PAGE.CONTENT.EXERCISES.REMOVED_FROM_FAVORITES'),
              fadeOut: true
            })
          } catch (error) {
            console.error('❌ Failed to unfavorite exercise', error)
            context.dispatch('showToast', {
              message: context.state.i18n.t('ERROR.GENERIC'),
              fadeOut: true
            })
          }
        },

        changeExerciseWeight: (context, { index, weight }) => {
          context.commit('changeExerciseWeight', { index, weight })
        },

        changeExerciseBreakpoint: (context, { index, value }) => {
          context.commit('changeExerciseBreakpoint', { index, value })
        },

        changeExerciseDuration: (context, { index, repetitionDuration }) => {
          context.commit('changeExerciseDuration', { index, repetitionDuration })
        },

        changeRepetitionsCount: (context, { index, repetitions }) => {
          context.commit('changeRepetitionsCount', { index, repetitions })
        },

        changeSubsequentRestPeriod: (context, { index, subsequentRestPeriod }) => {
          context.commit('changeSubsequentRestPeriod', { index, subsequentRestPeriod })
        },

        changeSide: (context, { index, side }) => {
          context.commit('changeSide', { index, side })
        },

        setWorkoutType: (context, workoutType) => {
          context.commit('setWorkoutType', workoutType)
        },

        setContinuousWorkout: (context, isContinuousWorkout) => {
          context.commit('setContinuousWorkout', isContinuousWorkout)
        },

        setCircleWorkout: (context, isCircleWorkout) => {
          context.commit('setCircleWorkout', isCircleWorkout)
        },

        setSelectedWorkout: (context, { workout, exercises }) => {
          context.commit('setSelectedWorkout', { workout, exercises })
        },

        setStationDependence: (context, { state, stationDependence }) => {
          context.commit('setStationDependence', { state, stationDependence })
        },

        setRestartWorkout: (context, restartWorkout) => {
          context.commit('setRestartWorkout', restartWorkout)
        },

        setSelectedTrainingPlan: (context, { trainingPlan }) => {
          context.commit('setSelectedTrainingPlan', { trainingPlan })
        },

        removeNewWorkout: (context) => {
          context.commit('removeNewWorkout')
        },

        setStations: (context, stations) => {
          context.commit('setStations', stations)
        },

        setAvailableWorkouts: (context, workouts) => {
          context.commit('setAvailableWorkouts', workouts)
        },

        setTrainingPlanDays: (context, days) => {
          context.commit('setTrainingPlanDays', days)
        },

        addWorkoutToTrainingDay: (context, workout) => {
          context.commit('addWorkoutToTrainingDay', workout)
        },

        removeNewTrainingPlan: (context) => {
          context.commit('removeNewTrainingPlan')
        },

        setExerciseTags: (context, exerciseTags) => {
          context.commit('setExerciseTags', exerciseTags)
        },

        setCompleteExerciseTags: (context, exerciseTags) => {
          context.commit('setCompleteExerciseTags', exerciseTags)
        },

        setNote: (context, note) => {
          context.commit('setNote', note)
        },

        setMemberForActiveTraining: (context, member: Member | null) => {
          context.commit('setMemberForActiveTraining', member)
        },

        setMemberForActiveWorkout: (context, member: Member | null) => {
          context.commit('setMemberForActiveWorkout', member)
        },

        setClub: (context, club) => {
          context.commit('setClub', club)
        },

        setActivities: (context, activities) => {
          context.commit('setActivities', activities)
        },

        setGoal: (context, goal) => {
          context.commit('setGoal', goal)
        },

        setCheckup: (context, checkup) => {
          context.commit('setCheckup', checkup)
        },

        addNoteIdToCheckup: (context, noteId: number) => {
          context.commit('addNoteIdToCheckup', noteId)
        },

        setPredefinedHealthGoals: (context, healthGoals) => {
          context.commit('setPredefinedHealthGoals', healthGoals)
        },

        setMemberWeightHeight: (context, { weight, height }) => {
          context.commit('setMemberWeightHeight', { weight, height })
        },

        setSelectedMember: (context, member: Member | null) => {
          context.commit('setSelectedMember', member)
        },

        updateActiveTrainingWorkouts: (context, newAtrributes: Object) => {
          context.commit('updateActiveTrainingWorkouts', newAtrributes)
        },

        setFilteredNotes: (context, notes: Note[]) => {
          context.commit('setFilteredNotes', notes)
        },

        addNotification: (context, notification: NotificationMessage) => {
          context.commit('addNotification', notification)
        },

        removeNotifications: (context) => {
          context.commit('removeNotifications')
        },

        setTask: (context, task: Task | null) => {
          context.commit('setTask', task)
        },

        setUser: (context, user) => {
          context.commit('setUser', user)
        },

        setClubStations: (context, stations) => {
          context.commit('setClubStations', stations)
        },

        setCheckupWorkout: (context, workoutId: number) => {
          context.commit('setCheckupWorkout', workoutId)
        },

        setCheckupTrainingPlan: (context, trainingPlanId: number) => {
          context.commit('setCheckupTrainingPlan', trainingPlanId)
        },

        setProfile: (context, memberProfile: MemberProfile) => {
          context.commit('setProfile', memberProfile)
        },

        setActivePlanStartDate: (context, startDate: string) => {
          context.commit('setActivePlanStartDate', startDate)
        },

        markTrainingPlanAsUpdated: (context) => {
          context.commit('markTrainingPlanAsUpdated')
        },

        changeStep: (context, step: number) => {
          context.commit('changeStep', step)
        },

        setOverviewMessage: (context, overviewMessage) => {
          context.commit('setOverviewMessage', overviewMessage)
        },

        filterMembers: (context, memberFilters: MemberFilters) => {
          context.commit('setMemberFilters', memberFilters)
        },

        filterMemberNotes: (context, noteFilters: NoteFilters) => {
          context.commit('setMemberNotesFilters', noteFilters)
        },

        filterWorkouts: (context, workoutFilters: WorkoutFilters) => {
          context.commit('setWorkoutFilters', workoutFilters)
        },

        filterExercises: (context, exerciseFilters: ExerciseFilters) => {
          context.commit('setExerciseFilters', exerciseFilters)
        },

        filterTrainingPlans: (context, trainingPlansFilters: TrainingPlanFilters) => {
          context.commit('setTrainingPlansFilters', trainingPlansFilters)
        },

        filterWorkoutSelector: (context, workoutSelectorFilters: WorkoutsSelectorFilters) => {
          context.commit('setWorkoutSelectorFilters', workoutSelectorFilters)
        },

        updateSidebarVisibility: (context, sidebarVisibility: boolean) => {
          context.commit('setSidebarVisibility', sidebarVisibility)
        },

        updateStatusScoreChartSelectedFilters: (context, statusScoreChartSelectedFilters: string[]) => {
          context.commit('setStatusScoreChartSelectedFilters', statusScoreChartSelectedFilters)
        },

        setUserClubSummary: (context, userClubSummary: UserClubSummary) => {
          context.commit('setUserClubSummary', userClubSummary)
        },

        setLeaderBoard: (context, leaderBoard: LeaderBoard) => {
          context.commit('setLeaderBoard', leaderBoard)
        },

        updateEmojiActiveType: (context, activeType: EmojiType) => {
          context.commit('setEmojiActiveType', activeType)
        },

        updateEmojiData: (context, data: EmojiData) => {
          context.commit('setEmojiData', data)
        },

        updateSelectedEmoji: (context, emoji: Emoji) => {
          context.commit('setSelectedEmoji', emoji)
        },

        updateExpandedMemberIndex: (context, index: number) => {
          context.commit('setExpandedMemberIndex', index)
        },

        updateDatepickerActiveTypeFilter: (context, filter: DateFilterType) => {
          context.commit('setDatepickerActiveTypeFilter', filter)
        },

        updateWorkoutLiveData: (context, data: WorkoutLiveData) => {
          context.commit('setWorkoutLiveData', data)
        },
        reorderWorkouts ({ commit }, { oldIndex, newIndex }) {
          commit('REORDER_WORKOUTS', { oldIndex, newIndex })
        }
      }
    })

    Store.configMoment(i18n.locale)
  }

  static configMoment (locale) {
    const momentLocale = locale ? locale.split('-')[0] : 'en'
    const spec = localeSpecifications[momentLocale] || localeSpecifications['en']
    moment.updateLocale(momentLocale, spec)
    moment.locale(momentLocale)
  }
}

export default Store
